import { TweenMax, TimelineLite } from 'gsap';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import * as focusTrap from 'focus-trap';

let menuItems = null;
let menu = null;
let menuInner = null;
let openMenuBtn = null;
let openSearchBtn = null;
let closeBtn = null;
const smBreakpoint = 768;
let trap = null;
let currentSubmenuTrap = null;
let openSubLinks = null;
let closeSubLinks = null;
let subMenuContainers = null;

const Navigation = {
    init() {
        menu = document.querySelector('.nav-primary');
        menuInner = document.querySelector('.nav-primary__inner');
        trap = focusTrap.createFocusTrap(menuInner);
        subMenuContainers = document.querySelectorAll('.nav-primary__sublist-container');

        if (menu !== null) {
            menuItems = [...menu.querySelectorAll('.nav-primary__item')];
            openMenuBtn = menu.querySelector('.js-nav-primary-open-menu');
            openSearchBtn = menu.querySelector('.js-nav-primary-open-search');
            closeBtn = menu.querySelector('.js-nav-primary-close');

            openSubLinks = [...menu.querySelectorAll('.js-open-submenu')];
            closeSubLinks = [...menu.querySelectorAll('.js-close-submenu')];

            if (openSubLinks) {
                openSubLinks.forEach(item => {
                    item.addEventListener('click', this.openSubMenu); // click on mobile
                    item.addEventListener('mouseenter', this.menuItemWithSubMouseEnter.bind(this));
                    item.addEventListener('focus', this.menuItemWithSubMouseEnter.bind(this));
                    item.addEventListener('mouseleave', this.menuItemWithSubMouseLeave.bind(this));
                    item.addEventListener('blur', this.menuItemWithSubMouseLeave.bind(this));

                    // This is a quick and dirty fix to close the submenu when the mouse leaves the submenu container
                    subMenuContainers.forEach(container =>
                        container.addEventListener('mouseleave', e => {
                            document.body.classList.remove('nav-primary-sublist--is-open');
                            e.target.ariaExpanded = false;
                            e.target
                                .closest('.nav-primary__item')
                                .querySelector('.nav-primary-sublist--is-open')
                                .classList.remove('nav-primary-sublist--is-open');
                        })
                    );

                    // If focus changes on the children of the item (links in the submenu), close the submenu
                    const subLinks = item
                        .closest('.nav-primary__item')
                        .querySelector('.nav-primary__sublist')
                        .querySelectorAll('a.nav-primary__sublink');

                    subLinks.forEach(subLink => {
                        subLink.addEventListener('blur', this.menuItemWithSubMouseLeave.bind(this));
                    });
                });
            }

            if (closeSubLinks) {
                closeSubLinks.forEach(subLink => {
                    subLink.addEventListener('click', this.closeSubMenu);
                });
            }

            openMenuBtn.addEventListener('click', this.openAnimation);
            closeBtn.addEventListener('click', this.closeAnimation);
        }

        window.addEventListener('keydown', e => {
            if (e.key === 'Escape') {
                this.closeAllSubmenus(e);
            }
        });
    },

    currentSubOpen: null,
    subLeaveTimeout: null,

    menuItemWithSubMouseEnter(e) {
        if (this.currentSubOpen) {
            if (this.currentSubOpen !== e.target) {
                this.currentSubOpen.classList.remove('nav-primary-sublist--is-open');
            } else {
                clearTimeout(this.subLeaveTimeout);
            }
        }

        document.body.classList.add('nav-primary-sublist--is-open');
        e.target.classList.add('nav-primary-sublist--is-open');
        e.target.ariaExpanded = true;
        this.currentSubOpen = e.target;
    },

    menuItemWithSubMouseLeave(e) {
        // If parent <li> is not hovered, AND if parent <li> has no focus-within (focus is not in the submenu)
        if (!e.target.closest('li.nav-primary__item:hover, li.nav-primary__item:focus-within')) {
            this.subLeaveTimeout = setTimeout(() => {
                e.target.classList.remove('nav-primary-sublist--is-open');
                e.target
                    .closest('.nav-primary__item')
                    .querySelector('.nav-primary-sublist--is-open')
                    .classList.remove('nav-primary-sublist--is-open');
                e.target.ariaExpanded = false;
            }, 250);

            document.body.classList.remove('nav-primary-sublist--is-open');
        }
    },

    openAnimation(e) {
        e.preventDefault();

        document.body.classList.add('nav-primary--is-open');
        Navigation.prepareAnimation();

        TweenMax.set(closeBtn, { display: 'block' });
        TweenMax.set(openMenuBtn, { display: 'none' });
        TweenMax.set(openSearchBtn, { display: 'none' });

        menuItems.forEach((item, index) => {
            TweenMax.to(item.closest('li.nav-primary__item'), 0.75, { opacity: 1, delay: 0.1 + index * 0.05 });
        });

        disableBodyScroll(document.body);
        trap.activate();
    },

    closeAnimation(e) {
        e.preventDefault();
        trap.deactivate();

        const tl = new TimelineLite({ onComplete: Navigation.reverseAnimation });
        tl.to(openMenuBtn, 0, { display: 'block' })
            .to(openSearchBtn, 0, { display: 'none' })
            .to(closeBtn, 0, { display: 'none' })
            .to(menuItems, 0.5, { opacity: 0 }, '-=0.60');
    },

    prepareAnimation() {
        TweenMax.set(menuItems, {
            opacity: 0,
        });
    },

    reverseAnimation() {
        Navigation.resetAnimation();
    },

    resetAnimation() {
        if (menu) {
            enableBodyScroll(document.body);
            document.body.classList.remove('nav-primary--is-open');

            if (window.isMobileLayout) {
                TweenMax.set(openMenuBtn, { clearProps: 'display' });
                TweenMax.set(openSearchBtn, { clearProps: 'display' });
                TweenMax.set(closeBtn, { display: 'none' });
            } else {
                TweenMax.set(openMenuBtn, { clearProps: 'display' });
                TweenMax.set(openSearchBtn, { clearProps: 'display' });
                TweenMax.set(openSubLinks, { opacity: 1 });
            }
        }
    },

    openSubMenu(e) {
        e.target.parentNode.classList.add('nav-primary__item--is-open');

        if (window.innerWidth < smBreakpoint) {
            e.preventDefault();
            currentSubmenuTrap = focusTrap.createFocusTrap(
                e.target.parentNode.querySelector('.nav-primary__sublist-container')
            );
            currentSubmenuTrap.activate();
        }
    },

    closeSubMenu(e) {
        e.preventDefault();
        if (currentSubmenuTrap) currentSubmenuTrap.deactivate();
        document.body.classList.remove('nav-primary-sublist--is-open');
        this.closest('.nav-primary__item').classList.remove('nav-primary__item--is-open');
    },

    closeAllSubmenus() {
        document.body.classList.remove('nav-primary-sublist--is-open');
        // Find all elements with "open" classes, and remove those classes
        const elsWithIsOpenClass = document.querySelectorAll(
            '.nav-primary-sublist--is-open, .nav-primary__item--is-open'
        );

        for (let i = 0; i < elsWithIsOpenClass.length; i += 1) {
            const cur = elsWithIsOpenClass[i];
            cur.classList.remove('nav-primary-sublist--is-open', 'nav-primary__item--is-open');
        }
    },
};

window.addEventListener('resize', () => {
    Navigation.resetAnimation();
});

export default Navigation;
